import { contrastRatio, mixColors, type StoreColors } from '@avrahami/contracts';

/**
 * The colours of a store's logo, and palettes made from them (adr/0034). Pure: it reads the
 * logo's RGBA pixels (a canvas's `ImageData.data`) and never touches the page, so the screen
 * draws the logo and this module decides.
 *
 * Only colourful pixels are candidates for the store's colour: transparent, near-white,
 * near-black and grey pixels are the logo's background and lettering, not its brand. The rest
 * are grouped into buckets of similar colour, merged into a few distinct colours, and ranked by
 * how much of the logo they cover times how colourful they are.
 */

/** The contrast a text colour needs on white (WCAG AA for body text). */
export const MIN_BRAND_CONTRAST = 4.5;
/** The dark colour the ink is mixed toward. */
const INK_BASE = '#0b0d10';
/** At most this many palettes are offered. */
const MAX_PALETTES = 4;
/** At most this many pixels are read: a big logo is sampled evenly. */
const MAX_SAMPLES = 40_000;
/** Two colours closer than this (RGB distance) are one colour of the logo. */
const MERGE_DISTANCE = 56;
/** Hues closer than this (degrees) are one hue: an accent needs another one. */
const DISTINCT_HUE = 28;
/** A colour that covers less than this share of the colourful pixels is a speck, not a colour. */
const MIN_SHARE = 0.02;

interface Hsl {
  /** 0–360 */
  h: number;
  /** 0–1 */
  s: number;
  /** 0–1 */
  l: number;
}

export interface LogoColor {
  hex: string;
  /** Share of the logo's colourful pixels, 0–1. */
  share: number;
  /** HSL saturation, 0–1. */
  saturation: number;
}

function toHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((channel) =>
      Math.round(Math.min(255, Math.max(0, channel)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

function rgbOf(hex: string): [number, number, number] {
  const value = Number.parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function hslOf(r: number, g: number, b: number): Hsl {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const delta = max - min;
  if (delta === 0) return { h: 0, s: 0, l };
  const s = delta / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === rn) h = ((gn - bn) / delta) % 6;
  else if (max === gn) h = (bn - rn) / delta + 2;
  else h = (rn - gn) / delta + 4;
  return { h: (h * 60 + 360) % 360, s, l };
}

function hexOfHsl({ h, s, l }: Hsl): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];
  return toHex((r + m) * 255, (g + m) * 255, (b + m) * 255);
}

const hslOfHex = (hex: string) => hslOf(...rgbOf(hex));

/** The distance between two hues on the colour wheel, 0–180 degrees. */
export function hueDistance(a: string, b: string): number {
  const difference = Math.abs(hslOfHex(a).h - hslOfHex(b).h) % 360;
  return difference > 180 ? 360 - difference : difference;
}

/** Background, lettering and greys: not a candidate for the store's colour. */
function isNeutral(r: number, g: number, b: number): boolean {
  const { s, l } = hslOf(r, g, b);
  return l > 0.93 || l < 0.08 || s < 0.2 || Math.max(r, g, b) - Math.min(r, g, b) < 24;
}

interface Bucket {
  count: number;
  r: number;
  g: number;
  b: number;
}

/**
 * The logo's distinct colours, the strongest first (coverage × saturation). Empty for a logo
 * with no colour of its own (black, white and grey only).
 */
export function logoColors(pixels: ArrayLike<number>): LogoColor[] {
  const total = Math.floor(pixels.length / 4);
  const step = Math.max(1, Math.ceil(total / MAX_SAMPLES));
  const buckets = new Map<number, Bucket>();
  let colourful = 0;
  for (let index = 0; index < total; index += step) {
    const offset = index * 4;
    const r = pixels[offset] ?? 0;
    const g = pixels[offset + 1] ?? 0;
    const b = pixels[offset + 2] ?? 0;
    const alpha = pixels[offset + 3] ?? 0;
    if (alpha < 128 || isNeutral(r, g, b)) continue;
    colourful += 1;
    // 16 levels a channel: shades of one colour fall in one bucket.
    const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
    const bucket = buckets.get(key) ?? { count: 0, r: 0, g: 0, b: 0 };
    bucket.count += 1;
    bucket.r += r;
    bucket.g += g;
    bucket.b += b;
    buckets.set(key, bucket);
  }
  if (colourful === 0) return [];

  // The biggest buckets first, each absorbing the smaller ones close to it.
  const clusters: Bucket[] = [];
  for (const bucket of [...buckets.values()].sort((x, y) => y.count - x.count)) {
    const [r, g, b] = [bucket.r / bucket.count, bucket.g / bucket.count, bucket.b / bucket.count];
    const near = clusters.find((cluster) => {
      const [cr, cg, cb] = [
        cluster.r / cluster.count,
        cluster.g / cluster.count,
        cluster.b / cluster.count,
      ];
      return Math.hypot(r - cr, g - cg, b - cb) < MERGE_DISTANCE;
    });
    if (near === undefined) clusters.push({ ...bucket });
    else {
      near.count += bucket.count;
      near.r += bucket.r;
      near.g += bucket.g;
      near.b += bucket.b;
    }
  }

  return clusters
    .map((cluster) => {
      const [r, g, b] = [
        cluster.r / cluster.count,
        cluster.g / cluster.count,
        cluster.b / cluster.count,
      ];
      return {
        hex: toHex(r, g, b),
        share: cluster.count / colourful,
        saturation: hslOf(r, g, b).s,
      };
    })
    .filter((color) => color.share >= MIN_SHARE)
    .sort((x, y) => y.share * y.saturation - x.share * x.saturation);
}

/** `hex` darkened step by step until it reads on white (or as dark as it gets). */
export function readableOnWhite(hex: string): string {
  let color = hex.toLowerCase();
  for (let step = 0; step < 40 && contrastRatio(color, '#ffffff') < MIN_BRAND_CONTRAST; step += 1) {
    color = mixColors(color, '#000000', 0.06);
  }
  return color;
}

/** The dark surfaces' colour: a very dark shade of the brand's hue. */
export function inkOf(brand: string): string {
  return mixColors(brand, INK_BASE, 0.82);
}

/**
 * A companion for a logo of one hue: warmer (its hue turned toward amber) and lighter, so the
 * markers stand out from the brand colour.
 */
export function companionOf(brand: string): string {
  const { h, s } = hslOfHex(brand);
  const toward = 38;
  // Turn the hue part of the way to amber, the short way round the wheel.
  const turn = ((toward - h + 540) % 360) - 180;
  const hue = (h + (Math.abs(turn) < 30 ? 160 : turn * 0.6) + 360) % 360;
  return hexOfHsl({ h: hue, s: Math.max(s, 0.7), l: 0.55 });
}

/**
 * Up to four palettes from a logo's colours: each takes one of the logo's strongest colours as
 * the brand (darkened until it reads on white), the strongest colour of another hue as the
 * accent (a warm, light companion when the logo has one hue), and a very dark shade of the
 * brand as the ink. Empty when the logo has no colour of its own.
 */
export function suggestPalettes(pixels: ArrayLike<number>): StoreColors[] {
  const colors = logoColors(pixels);
  const palettes: StoreColors[] = [];
  const add = (palette: StoreColors) => {
    if (palettes.length >= MAX_PALETTES) return;
    const same = palettes.some(
      (existing) => existing.brand === palette.brand && existing.accent === palette.accent,
    );
    if (!same) palettes.push(palette);
  };
  for (const base of colors) {
    const brand = readableOnWhite(base.hex);
    const others = colors.filter(
      (other) => other !== base && hueDistance(other.hex, base.hex) >= DISTINCT_HUE,
    );
    const accent = others[0]?.hex ?? companionOf(base.hex);
    add({ brand, accent, ink: inkOf(brand) });
  }
  // One hue only: the same brand with a second, lighter companion of its own hue.
  const first = colors[0];
  if (first !== undefined && palettes.length < 2) {
    const brand = readableOnWhite(first.hex);
    const { h, s } = hslOfHex(first.hex);
    add({ brand, accent: hexOfHsl({ h, s: Math.max(s, 0.6), l: 0.62 }), ink: inkOf(brand) });
  }
  return palettes;
}
